// © Thibault Sabathier, 2025. Tous droits réservés.
// components/ErrorBoundary.js - Gestionnaire d'erreurs global avec UI professionnelle
import React from 'react'
import {
  CContainer,
  CRow,
  CCol,
  CCard,
  CCardBody,
  CButton,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilReload, cilHome, cilWarning } from '@coreui/icons'
import { captureException } from '../utils/sentry'

// Détecte une erreur de CHARGEMENT de chunk lazy (vs un vrai bug de rendu).
// Cas typique : après un déploiement, l'ancien index.html en mémoire (onglet
// resté ouvert ou session PWA) tente d'importer un chunk JS au hash périmé →
// 404 → React.lazy throw. Ce n'est PAS un bug applicatif, c'est une mise à
// jour à absorber. Vite (TypeError "Failed to fetch dynamically imported
// module") et Webpack (ChunkLoadError) ont des signatures différentes.
const isChunkLoadError = (error) => {
  if (!error) return false
  const name = error.name || ''
  const msg = error.message || ''
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /dynamically imported module/i.test(msg)
  )
}

// Garde-fou anti-boucle : on n'auto-recharge qu'une fois par fenêtre de 20s.
// Si un chunk error survient à nouveau juste après un reload, c'est que le
// reload n'a pas résolu le problème (vrai bug, asset réellement absent) → on
// laisse l'écran d'erreur s'afficher plutôt que de boucler indéfiniment.
const RELOAD_GUARD_KEY = 'tl_chunk_reload_at'
const RELOAD_GUARD_MS = 20000

// Lecture/écriture du timestamp de garde. sessionStorage en priorité, fallback
// cookie : le cookie SURVIT au reload même quand sessionStorage est bloqué
// (mode privé strict, storage désactivé), ce qui préserve le garde-fou
// anti-boucle dans ce cas-là (une variable mémoire serait, elle, réinitialisée
// à chaque rechargement et ne protégerait donc de rien).
const readGuardTs = () => {
  try {
    const v = window.sessionStorage.getItem(RELOAD_GUARD_KEY)
    if (v != null) return Number(v)
  } catch {
    /* sessionStorage indisponible → fallback cookie ci-dessous */
  }
  const m = document.cookie.match(/(?:^|; )tl_chunk_reload_at=(\d+)/)
  return m ? Number(m[1]) : 0
}

const writeGuardTs = (ts) => {
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(ts))
    return
  } catch {
    /* sessionStorage indisponible → fallback cookie ci-dessous */
  }
  try {
    document.cookie = `${RELOAD_GUARD_KEY}=${ts}; path=/; max-age=${RELOAD_GUARD_MS / 1000}`
  } catch {
    /* storage ET cookies bloqués (environnement dégénéré) : on laisse passer */
  }
}

const shouldAutoReload = () => {
  const last = readGuardTs()
  if (Date.now() - last < RELOAD_GUARD_MS) return false
  writeGuardTs(Date.now())
  return true
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null, recovering: false }
  }

  static getDerivedStateFromError(error) {
    // recovering=true → on rend un écran neutre "Mise à jour" (pas l'écran
    // d'erreur anxiogène) le temps que le reload déclenché dans
    // componentDidCatch prenne effet.
    return { hasError: true, recovering: isChunkLoadError(error) }
  }

  componentDidCatch(error, errorInfo) {
    // Chunk périmé après déploiement : on recharge la page (récupère le nouvel
    // index.html → nouveaux hashs → tout repart) au lieu d'afficher une erreur.
    // Le client perçoit une mise à jour, pas un bug.
    if (isChunkLoadError(error)) {
      console.warn('[ErrorBoundary] Chunk load error (déploiement ?) → reload', error?.message)
      captureException(error instanceof Error ? error : new Error(String(error)), {
        tags: { source: 'chunk-load' },
        level: 'warning',
      })
      if (shouldAutoReload()) {
        window.location.reload()
        return
      }
      // Reload déjà tenté récemment sans succès → on tombe sur l'écran d'erreur
      // normal (en sortant du mode "recovering").
      this.setState({ error, errorInfo, recovering: false })
      return
    }

    this.setState({
      error: error,
      errorInfo: errorInfo,
    })
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    // Forward vers Sentry — sans ça, ce boundary swallow l'erreur AVANT que
    // Sentry.ErrorBoundary externe (index.js) puisse la capturer, ce qui crée
    // un trou d'observabilité sur tous les render crashes React.
    //
    // Format `contexts.react.componentStack` : c'est la convention Sentry
    // pour les erreurs React (déclenche le groupage stack-aware côté backend
    // Sentry et fait apparaître le component stack au bon endroit dans la UI).
    captureException(error, {
      contexts: {
        react: { componentStack: errorInfo?.componentStack },
      },
    })
  }

  handleRefresh = () => {
    window.location.reload()
  }

  handleGoHome = () => {
    window.location.href = '/dashboard'
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  render() {
    if (this.state.hasError) {
      // Chunk périmé en cours de récupération : écran neutre "Mise à jour"
      // pendant que window.location.reload() prend effet. Surtout PAS l'écran
      // d'erreur — le client ne doit pas croire que l'app est buguée alors
      // qu'il s'agit juste d'absorber un déploiement.
      if (this.state.recovering) {
        return (
          <div
            style={{
              minHeight: '60vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                border: '4px solid #e5e7eb',
                borderTopColor: '#1c2530',
                borderRadius: '50%',
                animation: 'tl-spin 0.8s linear infinite',
                marginBottom: '16px',
              }}
            />
            <p className="text-muted" style={{ fontSize: '14px', margin: 0 }}>
              Mise à jour de l'application…
            </p>
            <style>{'@keyframes tl-spin{to{transform:rotate(360deg)}}'}</style>
          </div>
        )
      }

      // Mode "inline" : encart compact pour une zone isolée (contenu d'une page),
      // afin qu'un plantage local ne déclenche PAS l'écran plein écran anxiogène.
      // Le layout (menu, header) reste debout.
      if (this.props.variant === 'inline') {
        return (
          <CCard className="border-0 shadow-sm my-4">
            <CCardBody className="text-center p-4 p-md-5">
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  background: '#fef3c7',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px',
                }}
              >
                <CIcon icon={cilWarning} size="xl" style={{ color: '#f59e0b' }} />
              </div>
              <h5 style={{ color: '#92400e', fontWeight: 700 }}>
                Ce contenu n'a pas pu s'afficher
              </h5>
              <p className="text-muted mb-4" style={{ fontSize: '14px' }}>
                Une erreur est survenue dans cette section. Le reste de l'application fonctionne
                normalement.
              </p>
              <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center">
                <CButton
                  color="primary"
                  onClick={this.handleRetry}
                  style={{ borderRadius: '10px', fontWeight: 600 }}
                >
                  <CIcon icon={cilReload} className="me-2" />
                  Réessayer
                </CButton>
                <CButton
                  color="secondary"
                  variant="outline"
                  onClick={this.handleGoHome}
                  style={{ borderRadius: '10px', fontWeight: 600 }}
                >
                  <CIcon icon={cilHome} className="me-2" />
                  Accueil
                </CButton>
              </div>
            </CCardBody>
          </CCard>
        )
      }

      return (
        <div
          style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <CContainer>
            <CRow className="justify-content-center">
              <CCol md={8} lg={6}>
                <CCard
                  className="border-0 shadow-lg"
                  style={{
                    borderRadius: '16px',
                    overflow: 'hidden',
                  }}
                >
                  {/* Header avec icône */}
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                      padding: '40px 30px',
                      textAlign: 'center',
                      borderBottom: '4px solid #f59e0b',
                    }}
                  >
                    <div
                      style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        background: '#f59e0b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px',
                      }}
                    >
                      <CIcon icon={cilWarning} size="3xl" style={{ color: 'white' }} />
                    </div>
                    <h2
                      style={{
                        color: '#92400e',
                        fontWeight: '700',
                        marginBottom: '10px',
                        fontSize: '24px',
                      }}
                    >
                      Oups ! Un problème est survenu
                    </h2>
                    <p
                      style={{
                        color: '#a16207',
                        marginBottom: 0,
                        fontSize: '14px',
                      }}
                    >
                      Pas d'inquiétude, cela arrive parfois
                    </p>
                  </div>

                  <CCardBody className="p-4 p-md-5 text-center">
                    <p
                      className="text-muted mb-4"
                      style={{ fontSize: '15px', lineHeight: '1.6' }}
                    >
                      L'application a rencontré une erreur inattendue.
                      <br />
                      Vous pouvez essayer de rafraîchir la page ou revenir à l'accueil.
                    </p>

                    <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                      <CButton
                        color="primary"
                        size="lg"
                        onClick={this.handleRefresh}
                        style={{
                          borderRadius: '10px',
                          fontWeight: '600',
                          padding: '12px 24px',
                        }}
                      >
                        <CIcon icon={cilReload} className="me-2" />
                        Rafraîchir la page
                      </CButton>

                      <CButton
                        color="secondary"
                        variant="outline"
                        size="lg"
                        onClick={this.handleGoHome}
                        style={{
                          borderRadius: '10px',
                          fontWeight: '600',
                          padding: '12px 24px',
                        }}
                      >
                        <CIcon icon={cilHome} className="me-2" />
                        Retour à l'accueil
                      </CButton>
                    </div>

                    {/* Bouton pour réessayer sans rafraîchir */}
                    <div className="mt-4">
                      <button
                        onClick={this.handleRetry}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#6b7280',
                          fontSize: '13px',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                      >
                        Réessayer sans rafraîchir
                      </button>
                    </div>

                    {/* Détails techniques en mode développement */}
                    {process.env.NODE_ENV === 'development' && this.state.error && (
                      <div className="mt-4 text-start">
                        <details
                          style={{
                            background: '#f9fafb',
                            borderRadius: '8px',
                            padding: '15px',
                            fontSize: '12px',
                            border: '1px solid #e5e7eb',
                          }}
                        >
                          <summary
                            style={{
                              cursor: 'pointer',
                              fontWeight: '600',
                              color: '#4b5563',
                              marginBottom: '10px',
                            }}
                          >
                            Détails techniques (dev)
                          </summary>
                          <pre
                            style={{
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              margin: 0,
                              color: '#ef4444',
                              fontFamily: 'monospace',
                            }}
                          >
                            {this.state.error.toString()}
                            {this.state.errorInfo?.componentStack}
                          </pre>
                        </details>
                      </div>
                    )}
                  </CCardBody>

                  {/* Footer */}
                  <div
                    style={{
                      background: '#f9fafb',
                      padding: '15px 30px',
                      textAlign: 'center',
                      borderTop: '1px solid #e5e7eb',
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        color: '#9ca3af',
                        fontSize: '12px',
                      }}
                    >
                      Si le problème persiste, contactez le support
                    </p>
                  </div>
                </CCard>
              </CCol>
            </CRow>
          </CContainer>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
